import { memo, useEffect, useMemo } from 'react';

import { useTrackActivatedIntercom, useTrackOpenIntercomChat } from '@air/analytics';
import { CircleQuestionMarkIcon } from '@air/design-icons';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { Routes } from '~/constants/routes';
import { NEW_TAB } from '~/constants/urls';
import { type IdentifyParams, useCanny } from '~/providers/CannyProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useWorkspaceMembersContext } from '~/providers/WorkspaceMembersProvider';
import { useBillableUsersCount } from '~/swr-hooks/members/useBillableUsersCount';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { CENTS_PER_DOLLAR } from '~/utils/CheckoutUtils';
import { getHelpMenuOptions } from '~/utils/menuOptions/getHelpMenuOptions';
import { getMonthlyPlanPriceCents } from '~/utils/SubscriptionUtils';

export const HelpButton = memo(() => {
  const [showDemoFormModal] = useAirModal(DemoFormModal);
  const { trackActivatedIntercom } = useTrackActivatedIntercom();
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();
  const { canny, getCannyUrl } = useCanny();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: subscription } = useSubscriptionContext();
  const { data: subscriptionPlan } = useSubscriptionPlan();
  const { totalBillableSeats } = useBillableUsersCount();
  const { data: workspaceMembers } = useWorkspaceMembersContext();
  const { data: storage } = useStorage();

  const planMonthlyTotalPrice = getMonthlyPlanPriceCents({
    subscription,
    plan: subscriptionPlan?.plan,
    totalBillableSeats,
  });

  const cannyParams = useMemo<Omit<IdentifyParams, 'appID'> | undefined>(() => {
    if (currentWorkspace && currentWorkspaceMember?.accountId) {
      return {
        user: {
          avatarURL: currentWorkspaceMember.avatar ?? '',
          email: currentWorkspaceMember.email,
          id: currentWorkspaceMember.accountId,
          name: `${currentWorkspaceMember.firstName} ${currentWorkspaceMember.lastName}`,
          companies: [
            {
              id: currentWorkspace.id,
              name: currentWorkspace.name,
              monthlySpend: planMonthlyTotalPrice ? planMonthlyTotalPrice / CENTS_PER_DOLLAR : 0,
              customFields: {
                plan: subscription?.plan,
                'Total Workspace Members': workspaceMembers?.length,
                'Total Workspace Storage': storage?.maxStorage,
              },
            },
          ],
        },
      };
    }
  }, [
    currentWorkspace,
    currentWorkspaceMember,
    planMonthlyTotalPrice,
    storage?.maxStorage,
    subscription?.plan,
    workspaceMembers?.length,
  ]);

  const cannyUrl = useMemo(() => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
    return cannyParams ? getCannyUrl(cannyParams) : undefined;
  }, [cannyParams, getCannyUrl]);

  useEffect(() => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
    if (cannyParams) {
      canny?.identify(cannyParams);
    }
  }, [canny, cannyParams]);

  const menuOptions = getHelpMenuOptions({
    onChatWithSales: () => showDemoFormModal(),
    onChatWithSupport: () => {
      trackActivatedIntercom({ location: 'main-nav' });
      trackOpenIntercomChat();
    },
    onHelpCenter: () => window.open(Routes.helpCenter, NEW_TAB),
    onLeaveFeedback: cannyUrl ? () => window.open(cannyUrl, NEW_TAB) : undefined,
    onTapNewAtAir: () => window.open(Routes.changelog, NEW_TAB),
  });

  return (
    <DropdownMenu
      trigger={
        <button
          className="ads:text-icon-default text-gray-10 ads:hover:text-icon-emphasis hover:text-gray-12"
          type="button"
        >
          <Tooltip label="Help" side="right">
            <CircleQuestionMarkIcon size="lg" />
          </Tooltip>
        </button>
      }
    >
      {renderDropdownItems({ options: menuOptions })}
    </DropdownMenu>
  );
});

HelpButton.displayName = 'HelpButton';
